import React, { useState } from 'react';
import { 
  Building2, 
  MapPin, 
  Clock,
  X,
  Upload,
  Star,
  BarChart2,
  Users,
  Calendar,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Phone,
  Mail,
  Globe
} from 'lucide-react';

// Add Provider Modal
export const AddProviderModal: React.FC<{
  providerType: 'legal' | 'surveyor' | 'inspector';
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ providerType, isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    registrationNumber: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    licenseNumber: '',
    serviceAreas: '',
    commission: '',
    documents: [] as File[]
  });

  const getProviderTypeIcon = () => {
    switch (providerType) {
      case 'legal':
        return Building2;
      case 'surveyor':
        return MapPin;
      case 'inspector':
        return Clock;
    }
  };

  const getRequiredDocuments = () => {
    const baseDocuments = [
      'Business Registration',
      'Tax Certificate',
      'Professional License'
    ];

    switch (providerType) {
      case 'legal':
        return [...baseDocuments, 'Bar Association License', 'Professional Indemnity Insurance'];
      case 'surveyor':
        return [...baseDocuments, 'Surveyor License', 'Equipment Certification'];
      case 'inspector':
        return [...baseDocuments, 'Inspector Certification', 'Insurance Certificate'];
    }
  };

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const Icon = getProviderTypeIcon();

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full m-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon className="h-6 w-6 text-gray-400" />
            <h2 className="text-lg font-medium text-gray-900">
              Add New {providerType.charAt(0).toUpperCase() + providerType.slice(1)} Provider
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Company/Provider Name
                </label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Registration Number
                </label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.registrationNumber}
                  onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  License Number
                </label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.licenseNumber}
                  onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Contact Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Website
                </label>
                <input
                  type="url"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Location and Service Areas */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Location & Service Areas</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Business Address
                </label>
                <textarea
                  required
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Service Areas
                </label>
                <textarea
                  required
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="List the areas where you provide services..."
                  value={formData.serviceAreas}
                  onChange={(e) => setFormData({ ...formData, serviceAreas: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Commission Structure */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Commission Structure</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Commission Rate (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.commission}
                  onChange={(e) => setFormData({ ...formData, commission: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Required Documents */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Required Documents</h3>
            
            <div className="space-y-4">
              {getRequiredDocuments().map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <span className="ml-3 text-sm text-gray-900">{doc}</span>
                  </div>
                  <input
                    type="file"
                    id={`doc-${index}`}
                    className="hidden"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files[0]) {
                        setFormData({
                          ...formData,
                          documents: [...formData.documents, files[0]]
                        });
                      }
                    }}
                  />
                  <label
                    htmlFor={`doc-${index}`}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                  >
                    <Upload className="h-4 w-4 mr-1.5" />
                    Upload
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Add Provider
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Provider Details Modal
export const ProviderDetailsModal: React.FC<{
  provider: any;
  isOpen: boolean;
  onClose: () => void;
}> = ({ provider, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full m-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Building2 className="h-6 w-6 text-gray-400" />
            <div>
              <h2 className="text-lg font-medium text-gray-900">{provider.name}</h2>
              <p className="text-sm text-gray-500">ID: {provider.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <Star className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Rating</p>
                  <p className="text-lg font-semibold text-gray-900">{provider.rating} / 5.0</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <BarChart2 className="h-5 w-5 text-green-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Success Rate</p>
                  <p className="text-lg font-semibold text-gray-900">{provider.successRate}%</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Jobs</p>
                  <p className="text-lg font-semibold text-gray-900">{provider.totalJobs}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Contact Information</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-gray-400" />
                <span className="ml-3 text-sm text-gray-900">{provider.email}</span>
              </div>
              <div className="flex items-center">
                <Phone className="h-5 w-5 text-gray-400" />
                <span className="ml-3 text-sm text-gray-900">{provider.phone}</span>
              </div>
              <div className="flex items-center">
                <Globe className="h-5 w-5 text-gray-400" />
                <span className="ml-3 text-sm text-gray-900">{provider.website}</span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-5 w-5 text-gray-400" />
                <span className="ml-3 text-sm text-gray-900">{provider.address}</span>
              </div>
            </div>
          </div>

          {/* Service Areas and Commission */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-4">Service Areas</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2">
                  {provider.serviceAreas.map((area: string, index: number) => (
                    <div key={index} className="flex items-center">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="ml-2 text-sm text-gray-900">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-4">Commission Structure</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                    <span className="ml-2 text-sm text-gray-900">Standard Rate</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{provider.commission}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Recent Activity</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-4">
                {provider.recentActivity?.map((activity: any, index: number) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                      <p className="text-sm text-gray-500">{activity.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Document Verification Status */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Document Verification Status</h3>
            <div className="space-y-4">
              {provider.documents?.map((doc: any, index: number) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <span className="ml-3 text-sm text-gray-900">{doc.type}</span>
                  </div>
                  <div className="flex items-center">
                    {doc.status === 'verified' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Verified
                      </span>
                    ) : doc.status === 'pending' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Clock className="h-4 w-4 mr-1" />
                        Pending
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        Rejected
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Reviews */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Recent Reviews</h3>
            <div className="space-y-4">
              {provider.reviews?.map((review: any, index: number) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              i < review.rating ? 'text-yellow-400' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {review.user}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">{review.date}</span>
                  </div>
                  <p className="text-sm text-gray-600">{review.comment}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {/* Handle suspension */}}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50"
            >
              Suspend Provider
            </button>
            <button
              onClick={() => {/* Handle editing */}}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Edit Details
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Review Modal Component
export const ProviderReviewModal: React.FC<{
  provider: any;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (review: any) => void;
}> = ({ provider, isOpen, onClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Review Provider</h2>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {/* Rating Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rating
              </label>
              <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRating(value)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`h-8 w-8 ${
                        value <= rating ? 'text-yellow-400' : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Review Comment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Review
              </label>
              <textarea
                rows={4}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience with this provider..."
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onSubmit({ rating, comment });
                onClose();
              }}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Submit Review
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default {
  AddProviderModal,
  ProviderDetailsModal,
  ProviderReviewModal
};